/**
 * Staff pet-record card: evaluation status chip + history + a quick
 * "record outcome" form for grandfathering a regular (migration 0099).
 * Reads GET /api/pets/:id/assessment; POSTs /api/pets/:id/assessment-outcome.
 */
import {
  type PetAssessmentView,
  getPetAssessment,
  recordPetAssessmentOutcome,
} from '@/lib/assessments';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AssessmentStatusChip } from './AssessmentOutcomePanel';

export function PetAssessmentPanel({ petId }: { petId: string }) {
  const [view, setView] = useState<PetAssessmentView | null>(null);
  const [outcome, setOutcome] = useState<'passed' | 'failed'>('passed');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await getPetAssessment(petId));
    } catch {
      setView(null);
    }
  }, [petId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!view) return null;
  const gated = view.settings.daycareRequiresAssessment || view.settings.boardingRequiresAssessment;
  if (!gated && view.status === 'not_required' && view.outcomes.length === 0) return null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await recordPetAssessmentOutcome(petId, {
        outcome,
        notes: notes.trim() || null,
        observations: null,
      });
      setNotes('');
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the outcome.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-surface" data-testid="pet-assessment-panel">
      <div className="flex items-center justify-between px-5 py-3">
        <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
          <ClipboardCheck className="size-3.5" strokeWidth={1.75} />
          Evaluation
        </p>
        <AssessmentStatusChip status={view.status} />
      </div>
      <div className="space-y-2 px-5 pb-4 text-sm">
        {view.status === 'passed' && (
          <p className="text-xs text-muted-foreground">
            Passed{' '}
            <span className="num">
              {view.passedAt ? new Date(view.passedAt).toLocaleDateString('en-GB') : ''}
            </span>
            {view.expiresAt ? (
              <>
                {' · valid until '}
                <span className="num">{new Date(view.expiresAt).toLocaleDateString('en-GB')}</span>
              </>
            ) : (
              ' · never expires'
            )}
          </p>
        )}
        {view.openBooking && (
          <p className="text-xs text-muted-foreground">
            Booked:{' '}
            <Link
              to={`/bookings/${view.openBooking.bookingId}`}
              className="font-medium text-primary hover:underline"
            >
              view evaluation booking
            </Link>
          </p>
        )}
        {view.outcomes.length > 0 && (
          <ul
            className="space-y-1 text-xs text-muted-foreground"
            data-testid="pet-assessment-history"
          >
            {view.outcomes.map((o) => (
              <li key={o.id}>
                <span className="font-medium text-foreground">
                  {o.outcome === 'passed' ? 'Passed' : 'Not passed'}
                </span>{' '}
                <span className="num">{new Date(o.createdAt).toLocaleDateString('en-GB')}</span>
                {o.notes ? ` — ${o.notes}` : ''}
              </li>
            ))}
          </ul>
        )}
        {!showForm ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            Record an outcome
          </button>
        ) : (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`pet-assessment-${petId}`}
                  checked={outcome === 'passed'}
                  onChange={() => setOutcome('passed')}
                />
                Passed
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`pet-assessment-${petId}`}
                  checked={outcome === 'failed'}
                  onChange={() => setOutcome('failed')}
                />
                Not passed
              </label>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              rows={2}
              maxLength={4000}
              aria-label="Outcome notes"
              placeholder="Notes for the owner"
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            />
            {error && (
              <p className="text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
              >
                {submitting && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
                Save outcome
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
